/**
 * API client for service mapping endpoints.
 * Maps GHL services to GHL calendars (service_id → calendar_id).
 */

import { authFetch, apiErrorMessage } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface ServiceMappingSuggestion {
    id: number;
    gh_id: string;
    service_external_id: string;
    service_name: string;
    suggested_calendar_external_id: string | null;
    suggested_calendar_name: string | null;
    confidence_score: number;
    reasoning: string;
    match_factors: Record<string, unknown> | null;
    alternative_suggestions: Array<{
        calendar_external_id: string;
        calendar_name: string;
        confidence_score: number;
        reasoning: string;
    }> | null;
    status: 'pending' | 'approved' | 'rejected';
    is_reviewed: boolean;
    reviewed_by: number | null;
    reviewed_by_name: string | null;
    reviewed_at: string | null;
    created_at: string;
}

export interface GHLServiceItem {
    id: number;
    external_id: string;
    name: string;
    description?: string;
    service_duration?: number;
    service_duration_unit?: string;
}

export interface GHLCalendarItem {
    id: number;
    external_id: string;
    name: string;
    description?: string;
    calendar_type?: string;
}

export interface StatsResponse {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
}

export interface ManualServiceMappingRequest {
    gh_id: string;
    service_external_id: string;
    service_name: string;
    calendar_external_id: string;
    calendar_name: string;
}

export const serviceMappingApi = {
    async triggerMapping(gh_id: string): Promise<{ status: string; message: string; suggestions_created: number }> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/trigger/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ gh_id }),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to trigger mapping'));
        }
        return response.json();
    },

    async getSuggestions(
        ghId: string,
        filters?: {
            status?: string;
            minConfidence?: number;
            maxConfidence?: number;
            limit?: number;
            offset?: number;
            serviceName?: string;
            calendarName?: string;
            search?: string;
        }
    ): Promise<{ total: number; suggestions: ServiceMappingSuggestion[] }> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.minConfidence !== undefined) params.append('min_confidence', filters.minConfidence.toString());
        if (filters?.maxConfidence !== undefined) params.append('max_confidence', filters.maxConfidence.toString());
        if (filters?.limit) params.append('limit', filters.limit.toString());
        if (filters?.offset) params.append('offset', filters.offset.toString());
        if (filters?.serviceName) params.append('service_name', filters.serviceName);
        if (filters?.calendarName) params.append('calendar_name', filters.calendarName);
        if (filters?.search) params.append('search', filters.search);

        const response = await authFetch(
            `${API_BASE_URL}/service-mapping/suggestions/${ghId}?${params.toString()}`
        );
        if (!response.ok) throw new Error('Failed to fetch suggestions');
        return response.json();
    },

    /**
     * Get distinct values per filterable column, for populating dropdown filters.
     */
    async getFilterOptions(ghId: string): Promise<{ service_name: string[]; calendar_name: string[] }> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/filter-options/${ghId}`);
        if (!response.ok) throw new Error('Failed to fetch filter options');
        return response.json();
    },

    async reviewSuggestion(id: number, action: 'approve' | 'reject', notes?: string): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/review/${id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action, notes }),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to review suggestion'));
        }
    },

    async deleteSuggestion(id: number): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to delete suggestion'));
        }
    },

    async getStats(ghId: string): Promise<StatsResponse> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/stats/${ghId}`);
        if (!response.ok) throw new Error('Failed to fetch stats');
        return response.json();
    },

    async getGHLServices(ghId: string): Promise<GHLServiceItem[]> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/ghl-services/${ghId}`);
        if (!response.ok) throw new Error('Failed to fetch Advital services');
        const data = await response.json();
        return data.services;
    },

    async getGHLCalendars(ghId: string): Promise<GHLCalendarItem[]> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/ghl-calendars/${ghId}`);
        if (!response.ok) throw new Error('Failed to fetch Advital calendars');
        const data = await response.json();
        return data.calendars;
    },

    async createManualMapping(request: ManualServiceMappingRequest): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/service-mapping/manual`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to create manual mapping'));
        }
    },
};
